<template>
  <div class="co-page">
    <div class="co-body">
      <!-- KPI Cards — 4列：前2个为跳转入口卡片，后2个为普通KPI -->
      <div class="co-kpi-row">
        <!-- ① 跳转 Cost Explorer -->
        <div class="co-entry-card" @click="$router.push({ name: 'costExplorer' })">
          <div class="co-entry-head">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2563eb" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>
            <span class="co-entry-title">Cost Explorer</span>
          </div>
          <p class="co-entry-desc">成本与用量分析，支持多维度分组、图表切换和明细导出</p>
          <div class="co-entry-link">进入模块 <svg class="co-arrow" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg></div>
        </div>

        <!-- ② 跳转 RI/SP 分析 -->
        <div class="co-entry-card" @click="$router.push({ name: 'rispSummary' })">
          <div class="co-entry-head">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
            <span class="co-entry-title">RI/SP 综合分析</span>
          </div>
          <p class="co-entry-desc">Reserved Instances &amp; Savings Plans 利用率、覆盖率与成本优化建议</p>
          <div class="co-entry-link">进入模块 <svg class="co-arrow" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg></div>
        </div>

        <!-- ③ 本月总费用 -->
        <div class="co-kpi-card">
          <div class="co-kpi-top">
            <span class="co-kpi-label">本月总费用</span>
          </div>
          <div class="co-kpi-value">{{ fmtCurrency(currentMonthTotal) }}</div>
          <div class="co-kpi-delta" v-if="totalChange !== null">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" :stroke="totalChange > 0 ? '#ef4444' : '#10b981'" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>
            <span :class="['co-kpi-pct', totalChange > 0 ? 'red' : 'green']">{{ totalChange > 0 ? '+' : '' }}{{ totalChange.toFixed(1) }}%</span>
            <span class="co-kpi-vs">vs 上月</span>
          </div>
        </div>

        <!-- ④ 告警概览 -->
        <div class="co-kpi-card">
          <div class="co-kpi-alert-top">
            <span class="co-kpi-label">告警概览</span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f87171" stroke-width="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
          </div>
          <div class="co-alert-badges">
            <div class="co-badge co-badge--critical">
              <span class="co-badge-num">{{ alertCritical }}</span>
              <span class="co-badge-label">严重</span>
            </div>
            <div class="co-badge co-badge--warning">
              <span class="co-badge-num">{{ alertWarning }}</span>
              <span class="co-badge-label">警告</span>
            </div>
            <div class="co-badge co-badge--info">
              <span class="co-badge-num">{{ alertInfo }}</span>
              <span class="co-badge-label">提示</span>
            </div>
          </div>
          <div class="co-link-sm" @click="$router.push({ name: 'costAlerts' })">
            查看全部告警 <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
          </div>
        </div>
      </div>

      <!-- Charts Row: 月度趋势(2/3) + 服务饼图(1/3) -->
      <div class="co-grid-2-1">
        <div class="co-card co-card--chart">
          <div class="co-card-head">
            <div>
              <h3 class="co-card-title">月度成本趋势</h3>
              <p class="co-card-sub">过去12个月按服务分类</p>
            </div>
            <div class="co-link-sm" @click="$router.push({ name: 'costExplorer' })">
              查看详情 <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
            </div>
          </div>
          <div ref="trendRef" class="co-chart" style="height:280px"></div>
        </div>

        <div class="co-card co-card--chart">
          <h3 class="co-card-title">本月服务分布</h3>
          <p class="co-card-sub" style="margin-bottom:12px">按服务类型占比</p>
          <div ref="pieRef" class="co-chart" style="height:200px"></div>
          <div class="co-legend">
            <div v-for="(item, i) in serviceData.slice(0, 5)" :key="item.name" class="co-legend-item">
              <div class="co-legend-left">
                <span class="co-legend-dot" :style="{ backgroundColor: COLORS[i] }"></span>
                <span class="co-legend-name">{{ item.name }}</span>
              </div>
              <span class="co-legend-val">{{ fmtCurrency(item.value) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Charts Row: RI/SP覆盖率(2/3) + 告警&快速入口(1/3) -->
      <div class="co-grid-2-1">
        <div class="co-card co-card--chart">
          <div class="co-card-head">
            <div>
              <h3 class="co-card-title">RI/SP 覆盖率趋势</h3>
              <p class="co-card-sub">过去12个月覆盖率变化</p>
            </div>
            <div class="co-link-sm" @click="$router.push({ name: 'rispSummary' })">
              查看详情 <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
            </div>
          </div>
          <div v-if="coverageTrend.length" ref="coverageRef" class="co-chart" style="height:220px"></div>
          <div v-else class="co-empty">暂无 RI/SP 覆盖率数据</div>
        </div>

        <div class="co-right-col">
          <!-- 最新告警 -->
          <div class="co-card co-card--compact">
            <div class="co-card-head" style="margin-bottom:12px">
              <h3 class="co-card-title" style="margin:0">最新告警</h3>
              <div class="co-link-sm" @click="$router.push({ name: 'costAlerts' })">查看全部</div>
            </div>
            <div class="co-alert-list" v-if="alertsTop3.length">
              <div v-for="a in alertsTop3" :key="a.id" class="co-alert-row">
                <span :class="['co-dot', severityColor(a.severity)]"></span>
                <div class="co-alert-info">
                  <div class="co-alert-title">{{ a.linkName }} {{ a.service ? '- ' + a.service : '' }}</div>
                  <div :class="['co-alert-pct', a.changePercent > 0 ? 'red' : 'green']">{{ a.changePercent > 0 ? '+' : '' }}{{ a.changePercent.toFixed(1) }}%</div>
                </div>
              </div>
            </div>
            <div v-else class="co-empty-sm">暂无告警</div>
          </div>

          <!-- 快速入口 -->
          <div class="co-card co-card--compact">
            <h3 class="co-card-title" style="margin-bottom:12px">快速入口</h3>
            <div class="co-quick-list">
              <div class="co-quick-item" @click="$router.push({ name: 'costAlerts' })">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                <div>
                  <div class="co-quick-name">费用告警</div>
                  <div class="co-quick-sub">{{ totalAlertCount }} 条告警待处理</div>
                </div>
              </div>
              <div class="co-quick-item" @click="$router.push({ name: 'rispSummary' })">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2563eb" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                <div>
                  <div class="co-quick-name">RI/SP 分析</div>
                  <div class="co-quick-sub">{{ expiringRI }} 个即将到期</div>
                </div>
              </div>
              <div class="co-quick-item" @click="$router.push({ name: 'rispSummary' })">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#10b981" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>
                <div>
                  <div class="co-quick-name">成本优化</div>
                  <div class="co-quick-sub">预计可节省 {{ fmtCurrency(totalSavings) }}/年</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 新增账号统计（伙伴/客户不可见，跨账号运营统计） -->
      <div v-if="!isLimited" class="co-card co-card--table">
        <div class="co-na-head">
          <div>
            <h3 class="co-card-title">新增账号统计</h3>
            <p class="co-card-sub">按自然周统计新增伙伴 / Payer / Link，区分 BT 与 Normal</p>
          </div>
          <button class="co-dl-btn" @click="downloadNewAccounts" :disabled="!naRows.length">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            下载
          </button>
        </div>

        <!-- 本周新增汇总条 -->
        <div class="co-na-summary">
          <div class="co-na-sum-item">
            <span class="co-na-sum-label">新增伙伴</span>
            <span class="co-na-sum-val">{{ naSummary.agents }}<span class="co-na-sum-bt" v-if="naSummary.agentsBt"> · BT {{ naSummary.agentsBt }}</span></span>
          </div>
          <div class="co-na-sum-item">
            <span class="co-na-sum-label">新增 Payer</span>
            <span class="co-na-sum-val">{{ naSummary.payers }}<span class="co-na-sum-bt" v-if="naSummary.payersBt"> · BT {{ naSummary.payersBt }}</span></span>
          </div>
          <div class="co-na-sum-item">
            <span class="co-na-sum-label">新增 Link</span>
            <span class="co-na-sum-val">{{ naSummary.links }}<span class="co-na-sum-bt" v-if="naSummary.linksBt"> · BT {{ naSummary.linksBt }}</span></span>
          </div>
        </div>

        <!-- 筛选区 -->
        <div class="co-na-filters">
          <el-date-picker
            v-model="naDateRange" type="daterange" size="small"
            range-separator="~" start-placeholder="开始日期" end-placeholder="结束日期"
            value-format="YYYY-MM-DD" :clearable="false" class="co-na-date"
            @change="loadNewAccounts" />
          <el-select v-model="naObjectType" filterable size="small" class="co-na-select" @change="loadNewAccounts">
            <el-option value="all" label="全部对象" />
            <el-option value="agent" label="伙伴" />
            <el-option value="payer" label="Payer" />
            <el-option value="link" label="Link" />
          </el-select>
          <el-select v-model="naAccountType" filterable size="small" class="co-na-select" @change="loadNewAccounts">
            <el-option value="all" label="全部类型" />
            <el-option value="bt" label="BT" />
            <el-option value="normal" label="Normal" />
          </el-select>
          <div class="co-na-search">
            <svg class="co-na-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
            <input v-model="naSearch" type="text" placeholder="搜索 代理商/Agent ID/Payer/Link ID" class="co-na-search-input" @keyup.enter="loadNewAccounts" />
          </div>
        </div>

        <div class="co-table-wrap" v-loading="naLoading">
          <table class="co-table">
            <thead>
              <tr>
                <th class="co-th">类型</th>
                <th class="co-th">账号类型</th>
                <th class="co-th">代理商</th>
                <th class="co-th">Agent ID</th>
                <th class="co-th">Payer</th>
                <th class="co-th">Payer ID</th>
                <th class="co-th">Link 客户</th>
                <th class="co-th">Link ID</th>
                <th class="co-th">销售</th>
                <th class="co-th">创建时间</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(r, i) in pagedNaRows" :key="i" class="co-tr">
                <td class="co-td"><span :class="['co-obj-tag', 'co-obj-' + r.objectType]">{{ objLabel(r.objectType) }}</span></td>
                <td class="co-td"><span :class="['co-at-tag', r.accountType === 'bt' ? 'co-at-bt' : 'co-at-normal']">{{ r.accountType === 'bt' ? 'BT' : 'Normal' }}</span></td>
                <td class="co-td">{{ r.agentName || '-' }}</td>
                <td class="co-td co-td--mono">{{ r.agentId || '-' }}</td>
                <td class="co-td">{{ r.payerName || '-' }}</td>
                <td class="co-td co-td--mono">{{ r.payerId || '-' }}</td>
                <td class="co-td">{{ r.linkName || '-' }}</td>
                <td class="co-td co-td--mono">{{ r.linkId || '-' }}</td>
                <td class="co-td">{{ r.sales || '-' }}</td>
                <td class="co-td co-td--nowrap">{{ r.createdAt || '-' }}</td>
              </tr>
              <tr v-if="!naRows.length && !naLoading" class="co-tr">
                <td class="co-td" colspan="10" style="text-align:center;color:#71717a">暂无数据</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-if="naRows.length > naPageSize" class="co-na-pagination">
          <el-pagination
            v-model:current-page="naPage"
            :page-size="naPageSize"
            :total="naRows.length"
            layout="total, prev, pager, next"
            small background />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import { ajaxGet } from '@/api/request'

const COLORS = ['#4a7fc1', '#7b68b8', '#3d9e7a', '#c4894a', '#c05c5c', '#3d8fa8', '#9aa5b4']

export default {
  name: 'CostOverview',
  data() {
    return {
      COLORS,
      // service trend data
      months: [],
      services: [],
      serviceDataMap: {},
      monthlyTotals: [],
      // derived
      currentMonthTotal: 0,
      totalChange: null,
      serviceData: [],
      // coverage trend
      coverageTrend: [],
      // alerts
      alertCritical: 0,
      alertWarning: 0,
      alertInfo: 0,
      activeAlerts: 0,
      totalAlertCount: 0,
      alertsTop3: [],
      // risp
      expiringRI: 0,
      totalSavings: 0,
      // new accounts table
      naDateRange: this._defaultRange(),
      naObjectType: 'all',
      naAccountType: 'all',
      naSearch: '',
      naRows: [],
      naSummary: { agents: 0, agentsBt: 0, payers: 0, payersBt: 0, links: 0, linksBt: 0 },
      naLoading: false,
      naPage: 1,
      naPageSize: 20,
      // chart instances
      _charts: {},
      _ro: null,
    }
  },
  computed: {
    isLimited() {
      const ui = JSON.parse(localStorage.getItem('userInfo') || '{}')
      const t = Number(ui.type) || 0
      return t === 2 || t === 4
    },
    pagedNaRows() {
      const start = (this.naPage - 1) * this.naPageSize
      return this.naRows.slice(start, start + this.naPageSize)
    },
  },
  watch: {
    naRows() { this.naPage = 1 },
  },
  methods: {
    _defaultRange() {
      // 最近 12 周（本周一往前推 11 周）到今天
      const today = new Date()
      const day = today.getDay() === 0 ? 7 : today.getDay() // 周一=1
      const monday = new Date(today)
      monday.setDate(today.getDate() - (day - 1) - 11 * 7)
      const fmt = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
      return [fmt(monday), fmt(today)]
    },
    objLabel(t) {
      return t === 'agent' ? '伙伴' : t === 'payer' ? 'Payer' : 'Link'
    },
    fmtCurrency(v) {
      if (v == null || isNaN(v)) return '$0'
      if (v >= 1000000) return '$' + (v / 1000000).toFixed(2) + 'M'
      if (v >= 1000) return '$' + (v / 1000).toFixed(1) + 'K'
      return '$' + Math.round(v).toLocaleString()
    },
    severityColor(s) {
      return s === 'critical' ? 'red' : s === 'warning' ? 'amber' : 'blue'
    },
    initChart(refName, key) {
      const el = this.$refs[refName]
      if (!el) return null
      if (this._charts[key]) this._charts[key].dispose()
      this._charts[key] = echarts.init(el)
      return this._charts[key]
    },

    async loadAllData() {
      await Promise.all([
        this.loadServiceTrend(),
        this.loadMonthlySales(),
        this.loadCoverageTrend(),
        this.loadAlerts(),
        this.loadRispKpi(),
        this.loadNewAccounts(),
      ])
    },

    // 0) 本月总费用 KPI — 与 Sales Overview 同源（tb_bill_sum）
    async loadMonthlySales() {
      try {
        const res = await ajaxGet({ url: '/stat/dashboard/monthly_sales_current/' })
        if (res && res.data) {
          this.currentMonthTotal = (res.data || []).reduce((s, r) => s + (r.sales_amount || 0), 0)
        }
      } catch (e) { console.error('loadMonthlySales', e) }
    },

    // 1) 月度成本趋势 + 本月服务分布 + 本月总费用 KPI
    async loadServiceTrend() {
      try {
        const res = await ajaxGet({ url: '/stat/cost_explorer/service_trend/', params: { top_n: 6 } })
        if (!res) return
        this.months = res.months || []
        this.services = res.services || []
        this.serviceDataMap = res.data || {}
        this.monthlyTotals = res.monthly_totals || []

        // Calculate change % from trend data (last two complete months)
        const len = this.monthlyTotals.length
        if (len >= 2 && this.monthlyTotals[len - 2] > 0) {
          this.totalChange = (this.monthlyTotals[len - 1] - this.monthlyTotals[len - 2]) / this.monthlyTotals[len - 2] * 100
        }

        // Pie data: last month's service breakdown
        if (this.months.length > 0) {
          const lastIdx = this.months.length - 1
          this.serviceData = this.services.map(svc => ({
            name: svc,
            value: (this.serviceDataMap[svc] || [])[lastIdx] || 0,
          })).sort((a, b) => b.value - a.value)
        }

        this.$nextTick(() => { this.renderTrend(); this.renderPie() })
      } catch (e) { console.error('loadServiceTrend', e) }
    },

    // 2) RI/SP 覆盖率趋势
    async loadCoverageTrend() {
      try {
        const res = await ajaxGet({ url: '/stat/risp/coverage_trend/' })
        if (res && res.data) {
          this.coverageTrend = res.data.trend || []
        }
        this.$nextTick(() => { this.renderCoverage() })
      } catch (e) { console.error('loadCoverageTrend', e) }
    },

    // 3) 告警 KPI + top3 列表
    async loadAlerts() {
      try {
        const [kpiRes, listRes] = await Promise.all([
          ajaxGet({ url: '/stat/alerts/kpi/' }).catch(() => null),
          ajaxGet({ url: '/stat/alerts/cost_anomaly/' }).catch(() => null),
        ])
        if (kpiRes && kpiRes.data) {
          this.alertCritical = kpiRes.data.critical || 0
          this.alertWarning = kpiRes.data.warning || 0
        }
        if (listRes && listRes.data) {
          const alerts = listRes.data.alerts || []
          this.totalAlertCount = alerts.length
          this.alertInfo = alerts.filter(a => a.severity === 'info').length
          this.alertsTop3 = alerts.slice(0, 3)
        }
      } catch (e) { console.error('loadAlerts', e) }
    },

    // 4) RI/SP KPI (expiring count + total savings)
    async loadRispKpi() {
      try {
        const res = await ajaxGet({ url: '/stat/risp/instances/' })
        if (res && res.data) {
          const kpi = res.data.kpi || {}
          this.expiringRI = kpi.expiringCount || 0
          this.totalSavings = (kpi.totalSavings || 0) * 12 // annualize monthly savings
        }
      } catch (e) { console.error('loadRispKpi', e) }
    },

    // 5) 新增账号统计（伙伴 / Payer / Link，区分 BT）
    async loadNewAccounts() {
      this.naLoading = true
      try {
        const params = {
          start_date: this.naDateRange && this.naDateRange[0] ? this.naDateRange[0] : '',
          end_date: this.naDateRange && this.naDateRange[1] ? this.naDateRange[1] : '',
          object_type: this.naObjectType,
          account_type: this.naAccountType,
          search: this.naSearch || '',
        }
        const res = await ajaxGet({ url: '/stat/dashboard/weekly_new_accounts/', params })
        if (res && res.data) {
          this.naRows = res.data.rows || []
          this.naSummary = res.data.summary || { agents: 0, agentsBt: 0, payers: 0, payersBt: 0, links: 0, linksBt: 0 }
        }
      } catch (e) {
        console.error('loadNewAccounts', e)
      } finally {
        this.naLoading = false
      }
    },

    downloadNewAccounts() {
      if (!this.naRows.length) return
      const headers = ['周期', '新增类型', '账号类型', '代理商名称', 'Agent ID', 'PayerName', 'Payer ID', 'Link客户名', 'Link ID', '销售', '创建时间']
      const lines = [headers]
      this.naRows.forEach(r => {
        lines.push([
          `${r.weekStart || ''}~${r.weekEnd || ''}`,
          this.objLabel(r.objectType),
          r.accountType === 'bt' ? 'BT' : 'Normal',
          r.agentName || '',
          r.agentId || '',
          r.payerName || '',
          r.payerId || '',
          r.linkName || '',
          r.linkId || '',
          r.sales || '',
          r.createdAt || '',
        ])
      })
      lines.push([])
      lines.push(['合计', '伙伴', '', this.naSummary.agents || 0, `BT ${this.naSummary.agentsBt || 0}`, 'Payer', this.naSummary.payers || 0, `BT ${this.naSummary.payersBt || 0}`, 'Link', this.naSummary.links || 0, `BT ${this.naSummary.linksBt || 0}`])
      const csv = lines.map(row => row.map(v => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`).join(',')).join('\n')
      const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `新增账号统计_${(this.naDateRange && this.naDateRange[0]) || ''}_${(this.naDateRange && this.naDateRange[1]) || ''}.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    },

    renderTrend() {
      const c = this.initChart('trendRef', 'trend')
      if (!c || !this.months.length) return
      const svcList = this.services
      if (!svcList.length) return
      c.setOption({
        tooltip: {
          trigger: 'axis', axisPointer: { type: 'shadow' },
          backgroundColor: '#fff', borderColor: '#e5e7eb', borderWidth: 1, borderRadius: 8,
          textStyle: { fontSize: 12, color: '#1b1b1f' },
          extraCssText: 'box-shadow:0 4px 12px rgba(0,0,0,0.08);',
          formatter: params => {
            let s = `<div style="font-weight:600;margin-bottom:4px">月份: ${params[0].axisValue}</div>`
            params.slice().reverse().forEach(p => {
              s += `<div style="display:flex;align-items:center;gap:6px;line-height:1.8">${p.marker}${p.seriesName}: ${this.fmtCurrency(p.value)}</div>`
            })
            return s
          },
        },
        legend: { data: svcList, bottom: 0, textStyle: { fontSize: 10, color: '#6b7280' }, itemWidth: 8, itemHeight: 8, itemGap: 6 },
        grid: { top: 12, left: 50, right: 16, bottom: 52 },
        xAxis: {
          type: 'category', data: this.months.map(m => m.length >= 6 ? m.slice(4) : m),
          axisLabel: { fontSize: 11, color: '#6b7280' },
          axisLine: { lineStyle: { color: '#e5e7eb' } }, axisTick: { show: false },
        },
        yAxis: {
          type: 'value',
          axisLabel: { formatter: v => `${(v / 1000).toFixed(0)}k`, fontSize: 11, color: '#6b7280' },
          axisLine: { show: false }, axisTick: { show: false },
          splitLine: { lineStyle: { color: '#e5e7eb', type: 'dashed' } },
        },
        series: svcList.map((svc, i) => ({
          name: svc, type: 'bar', stack: 'total',
          data: this.serviceDataMap[svc] || [],
          itemStyle: { color: COLORS[i % COLORS.length], borderRadius: i === svcList.length - 1 ? [3, 3, 0, 0] : 0 },
          barCategoryGap: '20%',
        })),
      })
    },

    renderPie() {
      const c = this.initChart('pieRef', 'pie')
      if (!c || !this.serviceData.length) return
      c.setOption({
        tooltip: {
          trigger: 'item',
          backgroundColor: '#fff', borderColor: '#e5e7eb', borderWidth: 1, borderRadius: 8,
          textStyle: { fontSize: 12, color: '#1b1b1f' },
          formatter: p => `${p.name}: ${this.fmtCurrency(p.value)} (${p.percent}%)`,
        },
        series: [{
          type: 'pie', radius: ['42%', '68%'], center: ['50%', '50%'],
          padAngle: 2, label: { show: false },
          emphasis: { itemStyle: { shadowBlur: 8, shadowColor: 'rgba(0,0,0,0.1)' } },
          data: this.serviceData.map((s, i) => ({ value: s.value, name: s.name, itemStyle: { color: COLORS[i % COLORS.length] } })),
        }],
      })
    },

    renderCoverage() {
      const c = this.initChart('coverageRef', 'coverage')
      if (!c || !this.coverageTrend.length) return
      const data = this.coverageTrend
      c.setOption({
        tooltip: {
          trigger: 'axis',
          backgroundColor: '#fff', borderColor: '#e5e7eb', borderWidth: 1, borderRadius: 8,
          textStyle: { fontSize: 12, color: '#1b1b1f' },
          formatter: params => params.map(p => `${p.marker}${p.seriesName}: ${p.value}%`).join('<br/>'),
        },
        legend: {
          data: ['RI 覆盖率', 'SP 覆盖率'], bottom: 0,
          textStyle: { fontSize: 11, color: '#6b7280' }, itemWidth: 12, itemHeight: 2,
        },
        grid: { top: 12, left: 44, right: 16, bottom: 44 },
        xAxis: {
          type: 'category', data: data.map(c => c.month.slice(5)),
          axisLabel: { fontSize: 11, color: '#6b7280' },
          axisLine: { lineStyle: { color: '#e5e7eb' } }, axisTick: { show: false },
        },
        yAxis: {
          type: 'value', min: 0, max: 100,
          axisLabel: { formatter: '{value}%', fontSize: 11, color: '#6b7280' },
          axisLine: { show: false }, axisTick: { show: false },
          splitLine: { lineStyle: { color: '#e5e7eb', type: 'dashed' } },
        },
        series: [
          {
            name: 'RI 覆盖率', type: 'line',
            symbol: data.length === 1 ? 'circle' : 'none',
            symbolSize: data.length === 1 ? 6 : 4,
            data: data.map(c => c.riCoverage),
            lineStyle: { color: '#22c55e', width: 2 }, itemStyle: { color: '#22c55e' },
            areaStyle: { color: '#22c55e', opacity: 0.08 },
          },
          {
            name: 'SP 覆盖率', type: 'line',
            symbol: data.length === 1 ? 'circle' : 'none',
            symbolSize: data.length === 1 ? 6 : 4,
            data: data.map(c => c.spCoverage),
            lineStyle: { color: '#7c3aed', width: 2 }, itemStyle: { color: '#7c3aed' },
            areaStyle: { color: '#7c3aed', opacity: 0.08 },
          },
        ],
      })
    },
  },

  mounted() {
    this.loadAllData()
    this._ro = new ResizeObserver(() => Object.values(this._charts).forEach(c => c && c.resize()))
    this.$nextTick(() => {
      ;['trendRef', 'pieRef', 'coverageRef'].forEach(r => {
        if (this.$refs[r]) this._ro.observe(this.$refs[r])
      })
    })
  },

  beforeUnmount() {
    if (this._ro) this._ro.disconnect()
    Object.values(this._charts).forEach(c => c && c.dispose())
  },
}
</script>

<style scoped>
/* ── Page ── */
.co-page {
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Helvetica Neue', 'Segoe UI', Arial, sans-serif;
  color: #1b1b1f;
  background: #f0f2f5 !important;
  min-height: 100%;
  font-size: 13px;
  -webkit-font-smoothing: antialiased;
}
.co-body {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* ── KPI Row (4 cols) ── */
.co-kpi-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  align-items: stretch;
}

/* Entry cards (clickable → Cost Explorer / RI-SP) */
.co-entry-card {
  background: #fff;
  border: 1px solid #e4e4e7;
  border-radius: 8px;
  padding: 16px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  transition: all 0.15s;
}
.co-entry-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  border-color: rgba(37, 99, 235, 0.4);
}
.co-entry-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}
.co-entry-title {
  font-size: 14px;
  font-weight: 600;
  color: #1b1b1f;
}
.co-entry-desc {
  font-size: 12px;
  color: #71717a;
  line-height: 1.5;
  flex: 1;
  margin: 0;
}
.co-entry-link {
  font-size: 12px;
  color: #2563eb;
  font-weight: 500;
  margin-top: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
}
.co-entry-card:hover .co-arrow {
  transform: translateX(4px);
  transition: transform 0.15s;
}

/* KPI cards */
.co-kpi-card {
  background: #fff;
  border: 1px solid #e4e4e7;
  border-radius: 8px;
  padding: 14px 16px;
}
.co-kpi-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 8px;
}
.co-kpi-alert-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
}
.co-kpi-label {
  font-size: 11px;
  font-weight: 500;
  color: #71717a;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.co-kpi-value {
  font-size: 24px;
  font-weight: 600;
  color: #1b1b1f;
  font-variant-numeric: tabular-nums;
  margin-bottom: 6px;
}
.co-kpi-delta {
  display: flex;
  align-items: center;
  gap: 6px;
}
.co-kpi-pct {
  font-size: 12px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
}
.co-kpi-pct.red { color: #ef4444; }
.co-kpi-pct.green { color: #10b981; }
.co-kpi-vs {
  font-size: 12px;
  color: #71717a;
}

/* Alert badges */
.co-alert-badges {
  display: flex;
  gap: 12px;
  margin-bottom: 10px;
}
.co-badge {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 48px;
}
.co-badge-num {
  font-size: 22px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  line-height: 1.2;
}
.co-badge-label {
  font-size: 10px;
  font-weight: 500;
  margin-top: 2px;
}
.co-badge--critical .co-badge-num { color: #ef4444; }
.co-badge--critical .co-badge-label { color: #ef4444; }
.co-badge--warning .co-badge-num { color: #f59e0b; }
.co-badge--warning .co-badge-label { color: #f59e0b; }
.co-badge--info .co-badge-num { color: #3b82f6; }
.co-badge--info .co-badge-label { color: #3b82f6; }

/* ── Grid layouts ── */
.co-grid-2-1 {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 16px;
}
.co-right-col {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* ── Cards ── */
.co-card {
  background: #fff;
  border: 1px solid #e4e4e7;
  border-radius: 8px;
  overflow: hidden;
}
.co-card--chart {
  padding: 20px;
}
.co-card--compact {
  padding: 16px;
}
.co-card--table {
  padding: 0;
}
.co-card-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
}
.co-card-title {
  font-size: 14px;
  font-weight: 600;
  color: #1b1b1f;
  margin: 0;
}
.co-card-sub {
  font-size: 12px;
  color: #71717a;
  margin-top: 2px;
}

/* ── Charts ── */
.co-chart {
  width: 100%;
}

/* ── Empty states ── */
.co-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 220px;
  color: #71717a;
  font-size: 13px;
}
.co-empty-sm {
  color: #71717a;
  font-size: 12px;
  text-align: center;
  padding: 16px 0;
}

/* ── Pie legend ── */
.co-legend {
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.co-legend-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 12px;
}
.co-legend-left {
  display: flex;
  align-items: center;
  gap: 8px;
}
.co-legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 2px;
  flex-shrink: 0;
}
.co-legend-name {
  color: #71717a;
}
.co-legend-val {
  color: #1b1b1f;
  font-variant-numeric: tabular-nums;
}

/* ── Alert list ── */
.co-alert-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.co-alert-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 12px;
}
.co-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
  margin-top: 6px;
}
.co-dot.red { background: #ef4444; }
.co-dot.amber { background: #f59e0b; }
.co-dot.blue { background: #3b82f6; }
.co-alert-info {
  min-width: 0;
}
.co-alert-title {
  font-weight: 500;
  color: #1b1b1f;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.co-alert-pct {
  font-variant-numeric: tabular-nums;
}
.co-alert-pct.red { color: #ef4444; }
.co-alert-pct.green { color: #10b981; }

/* ── Quick actions ── */
.co-quick-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.co-quick-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.1s;
}
.co-quick-item:hover {
  background: #f4f4f5;
}
.co-quick-name {
  font-size: 12px;
  font-weight: 500;
  color: #1b1b1f;
}
.co-quick-sub {
  font-size: 11px;
  color: #71717a;
}

/* ── Links ── */
.co-link-sm {
  font-size: 12px;
  color: #2563eb;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
}
.co-link-sm:hover {
  text-decoration: underline;
}
.co-link-text {
  font-weight: 500;
  color: #2563eb;
  cursor: pointer;
}
.co-link-text:hover {
  text-decoration: underline;
}

/* ── Table ── */
.co-table-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e4e4e7;
}
.co-table-wrap {
  overflow-x: auto;
}
.co-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}
.co-th {
  text-align: left;
  padding: 12px 20px;
  font-weight: 500;
  color: #71717a;
  white-space: nowrap;
  background: rgba(244, 244, 245, 0.3);
  border-bottom: 1px solid #e4e4e7;
}
.co-th--r {
  text-align: right;
}
.co-td {
  padding: 12px 20px;
  color: #1b1b1f;
  border-bottom: 1px solid #f4f4f5;
}
.co-td--r {
  text-align: right;
}
.co-td--num {
  font-variant-numeric: tabular-nums;
}
.co-td--bold {
  font-weight: 500;
}
.co-tr:last-child .co-td {
  border-bottom: none;
}
.co-tr:hover {
  background: rgba(244, 244, 245, 0.2);
  transition: background 0.15s;
}

/* Percent bar in table */
.co-pct-cell {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}
.co-pct-bar {
  width: 64px;
  height: 6px;
  background: #f4f4f5;
  border-radius: 99px;
  overflow: hidden;
}
.co-pct-fill {
  height: 100%;
  background: #2563eb;
  border-radius: 99px;
}

/* ── New accounts table ── */
.co-na-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e4e4e7;
}
.co-dl-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 14px;
  font-size: 12px;
  font-weight: 500;
  color: #1b1b1f;
  background: #fff;
  border: 1px solid #d4d4d8;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s;
}
.co-dl-btn:hover:not(:disabled) {
  background: #f4f4f5;
  border-color: #a1a1aa;
}
.co-dl-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.co-na-summary {
  display: flex;
  gap: 32px;
  padding: 14px 20px;
  border-bottom: 1px solid #f4f4f5;
}
.co-na-sum-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.co-na-sum-label {
  font-size: 11px;
  color: #71717a;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.co-na-sum-val {
  font-size: 20px;
  font-weight: 600;
  color: #1b1b1f;
  font-variant-numeric: tabular-nums;
}
.co-na-sum-bt {
  font-size: 12px;
  font-weight: 500;
  color: #c4894a;
}
.co-na-filters {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 20px;
  flex-wrap: wrap;
}
.co-na-date {
  width: 240px;
}
.co-na-select {
  width: 120px;
}
.co-na-search {
  position: relative;
  flex: 1;
  min-width: 220px;
  max-width: 320px;
}
.co-na-search-icon {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #a1a1aa;
}
.co-na-search-input {
  width: 100%;
  height: 32px;
  padding: 0 10px 0 30px;
  font-size: 12px;
  color: #1b1b1f;
  background: #f4f4f5;
  border: 1px solid transparent;
  border-radius: 8px;
  outline: none;
  box-sizing: border-box;
}
.co-na-search-input:focus {
  background: #fff;
  border-color: #2563eb;
}
.co-td--mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 11px;
  color: #52525b;
}
.co-td--nowrap {
  white-space: nowrap;
}
.co-obj-tag, .co-at-tag {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
}
.co-obj-agent { background: rgba(124, 58, 237, 0.1); color: #7c3aed; }
.co-obj-payer { background: rgba(37, 99, 235, 0.1); color: #2563eb; }
.co-obj-link { background: rgba(61, 158, 122, 0.12); color: #3d9e7a; }
.co-at-bt { background: rgba(196, 137, 74, 0.14); color: #b8742e; }
.co-at-normal { background: #f4f4f5; color: #71717a; }
</style>
